import { useEffect } from 'react';
import { recargarPagina } from '../utils/recargar';

/**
 * Recarga automática al detectar una nueva versión del frontend.
 *
 * El despliegue cambia el hash del chunk de entrada (`assets/index-*.js`):
 * si el que está corriendo difiere del que sirve el servidor, hay una
 * versión nueva y la pestaña se recarga para servirla. No hace falta
 * service worker ni versión en el backend: el propio `index.html`
 * (que nginx sirve con `Cache-Control: no-cache`) es la fuente de verdad.
 *
 * Solo recarga con la pestaña visible. En segundo plano espera a que el
 * usuario vuelva (`visibilitychange`): recargar a espaldas del paciente
 * borraría estado que no se puede recuperar (evaluaciones o formularios a
 * medio llenar).
 */

/** Cada 5 min. `index.html` son ~3 kB y va con `no-cache`: coste despreciable. */
const INTERVALO_MS = 5 * 60 * 1000;

/** Nombre del chunk de entrada que está corriendo ahora, ej. `index-Qrcs4OYk.js`. */
function chunkLocal(): string | null {
  const script = document.querySelector<HTMLScriptElement>('script[src*="/assets/index-"]');
  return script?.src.split('/').pop() ?? null;
}

/** Nombre del chunk de entrada que sirve el servidor ahora. `no-store` ignora
 *  cualquier caché intermedia; el regex captura el nombre hasheado, que cambia
 *  en cada build. */
async function chunkRemoto(): Promise<string | null> {
  const res = await fetch('/index.html', { cache: 'no-store' });
  const html = await res.text();
  return html.match(/assets\/index-[^"']+\.js/)?.[0]?.split('/').pop() ?? null;
}

export function useAutoUpdateCheck(): void {
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    let recargado = false;

    const verificar = async () => {
      if (recargado || document.visibilityState !== 'visible') return;
      try {
        const local = chunkLocal();
        const remoto = await chunkRemoto();
        if (local && remoto && local !== remoto) {
          recargado = true;
          recargarPagina();
        }
      } catch {
        // Red intermitente o error pasajero: se reintenta en el siguiente chequeo.
      }
    };

    const alVolver = () => {
      void verificar();
    };

    document.addEventListener('visibilitychange', alVolver);
    timer = setInterval(() => {
      void verificar();
    }, INTERVALO_MS);
    void verificar();

    return () => {
      document.removeEventListener('visibilitychange', alVolver);
      if (timer !== undefined) clearInterval(timer);
    };
  }, []);
}
